<template>
  <div class="mobile-container">
    <div class="search">
      <input v-model="query" @keyup.enter="search" placeholder="CIQ ID or Ticker" />
      <button @click="search">Search</button>
    </div>

    <div v-if="loading" class="loader">Loading…</div>
    <div v-if="error" class="error">{{ error }}</div>

    <div v-if="companyId" class="sections">
      <div class="card">
        <h2>{{ general.companyName }} <small v-if="general.Ticker">({{ general.Ticker }})</small></h2>
        <div class="sub">{{ general['Business Description'] }}</div>
      </div>

      <CrossReference :items="crossReference" />
      <Financials :bs="financials.bs" :is="financials.is" :cf="financials.cf" />
      <Transactions :items="transactions" :company-name="general.companyName" />
      <Ownership :items="ownership" />
      <CreditRatings :items="creditRatings" />
      <RXResearch :keys="rxResearch.keys" :abstract="rxResearch.abstract" />
    </div>
  </div>
</template>

<script>
import Financials from '@/mobile/dashboard2/Financials.vue';
import Transactions from '@/mobile/dashboard2/Transactions.vue';
import Ownership from '@/mobile/dashboard2/Ownership.vue';
import CreditRatings from '@/mobile/dashboard2/CreditRatings.vue';
import RXResearch from '@/mobile/dashboard2/RXResearch.vue';
import CrossReference from '@/mobile/dashboard2/CrossReference.vue';

import requestTemplateFinancials from 'raw-loader!@/assets/dashboard2/inputRequestsFinancials.template';
import requestTemplateTransactionList from 'raw-loader!@/assets/dashboard2/inputRequestsTransactionList.template';
import requestTemplateTransactionDetail from 'raw-loader!@/assets/dashboard2/inputRequestsTransactionDetail.template';
import requestTemplateOwnership from 'raw-loader!@/assets/dashboard2/inputRequestsOwnership.template';
import requestTemplateMarketCap from 'raw-loader!@/assets/dashboard2/inputRequestsMarketCap.template';
import api from '@/api/dashboard2.js';
import { _http } from '@/api/backend.js';

const jp = require('jsonpath');
const moment = require('moment');
const compile = require('es6-template-strings/compile');
const resolveToString = require('es6-template-strings/resolve-to-string');
const xpath = require('xpath');
const dom = require('@xmldom/xmldom').DOMParser;
const Buffer = require('buffer').Buffer;

export default {
  name: 'Dashboard2Mobile',
  components: { Financials, Transactions, Ownership, CreditRatings, RXResearch, CrossReference },
  data: () => ({
    query: '',
    companyId: '',
    error: null,
    loading: false,
    general: { companyName: '', Ticker: '', 'Business Description': '' },
    financials: { 
      bs: { totalAssets: [0,0], totalEquities: [0,0], currentAssets: [0,0], cashEquivalents: [0,0], totalLiabilities: [0,0], currentLiabilities: [0,0], periodDates: [null,null] },
      is: { totalRevenues: [0,0], costOfSales: [0,0], grossProfit: [0,0], grossMargin: [0,0], totalOperationProfit: [0,0], ebitda: [0,0], ebitdaMargin: [0,0], ebit: [0,0], ebitMargin: [0,0], netIncome: [0,0], netMargin: [0,0], eps: [0,0], periodDates: [null,null] },
      cf: { cashflowOperating: [0,0], cashflowInvesting: [0,0], cashflowFinancing: [0,0], changeInCash: [0,0], capitalExpenditure: [0,0], periodDates: [null,null] }
    },
    transactions: [],
    ownership: [],
    creditRatings: [],
    rxResearch: { keys: [], abstract: '', articlePublishedDate: null },
    crossReference: [],
    marketCap: { amount: 0, asOfDate: null, changeQoQ: 0, changeYoY: 0 },
  }),
  methods: {
    async search() {
      if (!this.query) return;
      this.loading = true; this.error = null;
      try {
        await this.fetchGeneral(this.query);
        await Promise.allSettled([
          this.fetchFinancials(this.query),
          this.fetchMarketCap(this.query),
          this.fetchCrossReference(this.companyId),
          this.fetchTransactions(this.companyId),
          this.fetchOwnership(this.companyId),
          this.fetchCreditRating(this.companyId),
          this.fetchRXResearch(this.companyId),
        ]);
      } catch (e) {
        this.error = (e && e.message) ? e.message : String(e);
      }
      this.loading = false;
    },

    async fetchGeneral(query) {
      const accessToken = localStorage.getItem('accessToken');
      let response = await _http(accessToken).post('/gdsapi/rest/v3/clientservice.json', {
        inputRequests: [
          { function: 'GDSP', identifier: query, mnemonic: 'IQ_COMPANY_NAME' },
          { function: 'GDSP', identifier: query, mnemonic: 'IQ_COMPANY_TICKER' },
          { function: 'GDSP', identifier: query, mnemonic: 'IQ_BUSINESS_DESCRIPTION' },
          { function: 'GDSP', identifier: query, mnemonic: 'IQ_COMPANY_ID' },
        ],
      });
      const r = jp.query(response.data, '$..Row[0]');
      this.general.companyName = r[0];
      this.general.Ticker = r[1];
      this.general['Business Description'] = r[2];
      this.companyId = r[3];
    },

    async fetchFinancials(query) {
      const compiled = compile(requestTemplateFinancials);
      const inputRequests = JSON.parse(resolveToString(compiled, { query }));
      const accessToken = localStorage.getItem('accessToken');
      let resp = await _http(accessToken).post('/gdsapi/rest/v3/clientservice.json', inputRequests);
      this.financials.bs.totalAssets = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_TOTAL_ASSETS')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.bs.currentAssets = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_TOTAL_CA')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.bs.cashEquivalents = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_CASH_EQUIV')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.bs.totalLiabilities = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_TOTAL_LIAB')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.bs.currentLiabilities = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_TOTAL_CL')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.bs.totalEquities = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_TOTAL_EQUITY')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.bs.periodDates = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_PERIODDATE_BS')]..Row[1]").map(x => moment(x, 'MM/DD/YYYY'));
      // Income statement
      this.financials.is.totalRevenues = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_TOTAL_REV')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.is.costOfSales = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_COGS')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.is.grossProfit = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_GP')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.is.grossMargin = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_GROSS_MARGIN')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.is.totalOperationProfit = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_OPER_INC')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.is.ebitda = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_EBITDA')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.is.ebitdaMargin = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_EBITDA_MARGIN')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.is.ebit = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_EBIT')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.is.ebitMargin = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_EBIT_MARGIN')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.is.netIncome = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_NI')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.is.netMargin = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_NI_MARGIN')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.is.eps = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_DILUT_EPS_EXCL')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.is.periodDates = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_PERIODDATE_IS')]..Row[1]").map(x => moment(x, 'MM/DD/YYYY'));
      // Cash flow
      this.financials.cf.cashflowOperating = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_CASH_OPER')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.cf.cashflowInvesting = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_CASH_INVEST')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.cf.cashflowFinancing = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_CASH_FINAN')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.cf.changeInCash = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_NET_CHANGE')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.cf.capitalExpenditure = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_CAPEX')]..Row[0]").map(x => parseFloat(x)||0);
      this.financials.cf.periodDates = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_PERIODDATE_CF')]..Row[1]").map(x => moment(x, 'MM/DD/YYYY'));
    },

    async fetchMarketCap(query) {
      const asOfDate = moment();
      const compiled = compile(requestTemplateMarketCap);
      const inputRequests = JSON.parse(resolveToString(compiled, { query, today: moment(asOfDate).format('MM/DD/YYYY'), beforeMonth: moment(asOfDate).subtract(1, 'months').format('MM/DD/YYYY'), beforeYear: moment(asOfDate).subtract(1, 'years').format('MM/DD/YYYY') }));
      const accessToken = localStorage.getItem('accessToken');
      let resp = await _http(accessToken).post('/gdsapi/rest/v3/clientservice.json', inputRequests);
      const raw = jp.query(resp.data, '$..Row[0]').map(x => parseFloat(x)||0);
      this.marketCap.amount = raw[0] || 0;
      this.marketCap.asOfDate = asOfDate;
      this.marketCap.changeQoQ = raw[1] ? (1 - raw[1] / raw[0]) * 100 : 0;
      this.marketCap.changeYoY = raw[2] ? (1 - raw[2] / raw[0]) * 100 : 0;
    },

    async fetchCrossReference(companyId) {
      this.crossReference.splice(0, this.crossReference.length);
      if (!companyId) return;
      let inputRequests = [
        'BECRS_ENTITY_BASE',
        'BECRS_ENTITY_FITCH',
        'BECRS_ENTITY_MARKIT_RED',
        'BECRS_ENTITY_MOODY',
        'BECRS_ENTITY_RX',
        'BECRS_ENTITY_BIC_CODE',
      ].map((m) => ({ function: 'GDSPV', identifier: companyId, mnemonic: m }));

      const accessToken = localStorage.getItem('accessToken');
      let resp = await _http(accessToken).post('/gdsapi/rest/v3/clientservice.json', { inputRequests: inputRequests });
      let entityBase = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic == 'BECRS_ENTITY_BASE')].Rows[?(@.Row[4] == '1' && @.Row[5] == '1')].Row").map((x) => ({ identifierTypeName: x[2], identifierTypeValue: x[3] }));
      let entityFitch = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic == 'BECRS_ENTITY_FITCH')].Rows[?(@.Row[4] == '1' && @.Row[5] == '1')].Row").map((x) => ({ identifierTypeName: x[2], identifierTypeValue: x[3] }));
      let entityMarkitRed = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic == 'BECRS_ENTITY_MARKIT_RED')].Rows[?(@.Row[4] == '1' && @.Row[5] == '1')].Row").map((x) => ({ identifierTypeName: x[2], identifierTypeValue: x[3] }));
      let entityMoody = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic == 'BECRS_ENTITY_MOODY')].Rows[?(@.Row[4] == '1' && @.Row[5] == '1')].Row").map((x) => ({ identifierTypeName: x[2], identifierTypeValue: x[3] }));
      let entityRX = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic == 'BECRS_ENTITY_RX')].Rows[?(@.Row[4] == '1' && @.Row[5] == '1')].Row").map((x) => ({ identifierTypeName: x[2], identifierTypeValue: x[3] }));
      let entityBIC = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic == 'BECRS_ENTITY_BIC_CODE')].Rows[?(@.Row[4] == '1' && @.Row[5] == '1')].Row").map((x) => ({ identifierTypeName: x[2], identifierTypeValue: x[3] }));
      this.crossReference = [ ...entityBase, ...entityFitch, ...entityMarkitRed, ...entityMoody, ...entityRX, ...entityBIC ];
    },

    async fetchTransactions(companyId) {
      this.transactions.splice(0, this.transactions.length);
      if (!companyId) return;
      let compiled = compile(requestTemplateTransactionList);
      let inputRequests = JSON.parse(resolveToString(compiled, { query: companyId }));
      const accessToken = localStorage.getItem('accessToken');
      let resp = await _http(accessToken).post('/gdsapi/rest/v3/clientservice.json', inputRequests);
      let transactionIds = jp.query(resp.data, '$..Row[0]');
      if (!transactionIds || transactionIds.length === 0) return;
      inputRequests = [];
      for (let transactionId of transactionIds) {
        let compiled2 = compile(requestTemplateTransactionDetail);
        inputRequests = inputRequests.concat(JSON.parse(resolveToString(compiled2, { transactionId })));
      }
      resp = await _http(accessToken).post('/gdsapi/rest/v3/clientservice.json', { inputRequests });
      const transactionDetail = jp.query(resp.data, '$..Row[0]');
      const chunkSize = inputRequests.length / transactionIds.length;
      for (let i = 0; i < transactionDetail.length; i += chunkSize) {
        let raw = transactionDetail.slice(i, i + chunkSize);
        let transaction = {
          announceDate: moment(raw[0], 'MM/DD/YYYY'),
          transactionType: raw[1],
          transactionValue: parseFloat(raw[2]),
          targetName: raw[3],
          buyerName: raw[4],
          raised: parseFloat(raw[5]),
          preMoney: parseFloat(raw[6]),
          postMoney: parseFloat(raw[7]),
          status: raw[8],
        };
        this.transactions.push(transaction);
      }
    },

    async fetchOwnership(companyId) {
      this.ownership.splice(0, this.ownership.length);
      if (!companyId) return;
      let compiled = compile(requestTemplateOwnership);
      let inputRequests = JSON.parse(resolveToString(compiled, { query: companyId }));
      const accessToken = localStorage.getItem('accessToken');
      let resp = await _http(accessToken).post('/gdsapi/rest/v3/clientservice.json', inputRequests);
      let ciqIds = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_HOLDER_CIQID')]..Row[0]");
      let holderNames = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_HOLDER_NAME')]..Row[0]");
      let holderShares = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_HOLDER_SHARES')]..Row[0]").map(x => parseFloat(x)||0);
      let holderPercent = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_HOLDER_PERCENT')]..Row[0]").map(x => parseFloat(x)||0);
      let positionDates = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_HOLDER_POSITION_DATE')]..Row[0]").map(x => moment(x, 'YYYY-MM-DD'));
      let holderValues = jp.query(resp.data, "GDSSDKResponse[?(@.Mnemonic=='IQ_HOLDER_VALUE')]..Row[0]").map(x => parseFloat(x)||0);

      // holdingsEquityAssets
      inputRequests = ciqIds.map((id) => ({ function: 'GDSP', identifier: id, mnemonic: 'IQ_HOLDINGS_EQUITY_ASSETS', properties: { currencyId: 'USD' } }));
      resp = await _http(accessToken).post('/gdsapi/rest/v3/clientservice.json', { inputRequests: inputRequests });
      let holderEquities = jp.query(resp.data, '$..Row[0]').map((x) => parseFloat(x)||0);

      // holder shares for last quarter
      inputRequests = positionDates.map((positionDate, index) => ({ function: 'GDSP', identifier: companyId, mnemonic: 'IQ_HOLDER_SHARES', properties: { asOfDate: moment(positionDate).subtract(3, 'months').format('YYYY/MM/DD'), rank: String(index + 1) } }));
      resp = await _http(accessToken).post('/gdsapi/rest/v3/clientservice.json', { inputRequests: inputRequests });
      let holderSharesLastQuarter = jp.query(resp.data, '$..Row[0]').map((x) => parseFloat(x)||0);

      for (let i=0;i<ciqIds.length;i++){
        let ownership = {
          ciqId: ciqIds[i],
          holderName: holderNames[i],
          holderShare: holderShares[i],
          holderValue: holderValues[i],
          holderEquity: holderEquities[i],
          holderPercent: holderPercent[i],
          changeQoQ: holderShares[i] - (holderSharesLastQuarter[i] || 0),
          positionDate: positionDates[i],
        };
        this.ownership.push(ownership);
      }
    },

    async fetchCreditRating(companyId) {
      this.creditRatings.splice(0, this.creditRatings.length);
      if (!companyId) return;
      const accessToken = localStorage.getItem('accessToken');
      let resp = await api.fetchCreditRating(companyId, accessToken);
      if (resp && resp.error) return;
      // Desktop code expects nested issuerRatingsResponse / issuerCwolResponse fields
      const r = resp.issuerRatingsResponse || resp;
      const c = resp.issuerCwolResponse || {};
      this.creditRatings.push({
        deptType: 'Foreign Currency LT',
        rating: r.issuerCreditRatingForeignCurrencyLongTerm,
        ratingDate: moment(r.issuerCreditRatingForeignCurrencyLongTermDate, 'YYYY-MM-DD HH:mm:ss'),
        lastReviewDate: moment(r.issuerCreditRatingForeignCurrencyLongTermLastReviewDate, 'YYYY-MM-DD HH:mm:ss'),
        cwol: c.issuerCreditRatingForeignCurrencyLongTermCWOL,
        cwolDate: moment(c.issuerCreditRatingForeignCurrencyLongTermCWOLDate, 'YYYY-MM-DD HH:mm:ss')
      });
      this.creditRatings.push({
        deptType: 'Foreign Currency ST',
        rating: r.issuerCreditRatingForeignCurrencyShortTerm,
        ratingDate: moment(r.issuerCreditRatingForeignCurrencyShortTermDate, 'YYYY-MM-DD HH:mm:ss'),
        lastReviewDate: moment(r.issuerCreditRatingForeignCurrencyShortTermLastReviewDate, 'YYYY-MM-DD HH:mm:ss'),
        cwol: c.issuerCreditRatingForeignCurrencyShortTermCWOL,
        cwolDate: moment(c.issuerCreditRatingForeignCurrencyShortTermCWOLDate, 'YYYY-MM-DD HH:mm:ss')
      });
      this.creditRatings.push({
        deptType: 'Local Currency LT',
        rating: r.issuerCreditRatingLocalCurrencyLongTerm,
        ratingDate: moment(r.issuerCreditRatingLocalCurrencyLongTermDate, 'YYYY-MM-DD HH:mm:ss'),
        lastReviewDate: moment(r.issuerCreditRatingLocalCurrencyLongTermLastReviewDate, 'YYYY-MM-DD HH:mm:ss'),
        cwol: c.issuerCreditRatingLocalCurrencyLongTermCWOL,
        cwolDate: moment(c.issuerCreditRatingLocalCurrencyLongTermCWOLDate, 'YYYY-MM-DD HH:mm:ss')
      });
      this.creditRatings.push({
        deptType: 'Local Currency ST',
        rating: r.issuerCreditRatingLocalCurrencyShortTerm,
        ratingDate: moment(r.issuerCreditRatingLocalCurrencyShortTermDate, 'YYYY-MM-DD HH:mm:ss'),
        lastReviewDate: moment(r.issuerCreditRatingLocalCurrencyShortTermLastReviewDate, 'YYYY-MM-DD HH:mm:ss'),
        cwol: c.issuerCreditRatingLocalCurrencyShortTermCWOL,
        cwolDate: moment(c.issuerCreditRatingLocalCurrencyShortTermCWOLDate, 'YYYY-MM-DD HH:mm:ss')
      });
    },

    async fetchRXResearch(companyId) {
      this.rxResearch.keys.splice(0, this.rxResearch.keys.length);
      this.rxResearch.abstract = '';
      if (!companyId) return;
      const accessToken = localStorage.getItem('accessToken');
      let response = await api.fetchArticleList(companyId, accessToken);
      if (response.error || !response.length) return;
      let articleId = response[0].articleId;
      this.rxResearch.articlePublishedDate = moment(response[0].articlePublishedDate, 'MM/DD/YYYY');
      response = await api.fetchArticleContent(articleId, accessToken);
      if (response.error) return;
      const buffer = Buffer.from(response, 'base64');
      const xml = buffer.toString('utf-8');
      const doc = new dom().parseFromString(xml, 'text/xml');
      let rows = xpath.select('/page/ROWSET/ARTICLE/spdoc/analysis/body/section[@name="Credit Highlights"]/section[1]/table/tablerow', doc);
      let contentIndex = 2;
      if (rows.length === 0) {
        rows = xpath.select('/page/ROWSET/ARTICLE/spdoc/analysis/body/section[@name="Credit Highlights"]/table/tablerow', doc);
        contentIndex = 3;
      }
      let keys = [];
      let abstract = `(Publish date: ${this.rxResearch.articlePublishedDate.format('DD-MMM-YYYY')}')<br />`;
      rows.forEach((row, index) => {
        if (index < contentIndex) return;
        let k = { keyStrength: row.childNodes[1].textContent, keyRisk: row.childNodes[3].textContent };
        keys.push(k);
      });
      if (keys.length === 0) keys = [{ keyStrength: 'No key strength/risk section' }];
      this.rxResearch.keys = keys;
      let section2 = xpath.select('/page/ROWSET/ARTICLE/spdoc/analysis/body/section[@name="Credit Highlights"]/section[2]', doc)[0];
      if (section2 == undefined) section2 = xpath.select('/page/ROWSET/ARTICLE/spdoc/analysis/body/section[@name="Credit Highlights"]', doc)[0];
      if (section2 == undefined) { abstract += '(No credit highlights section)'; this.rxResearch.abstract = abstract; return; }
      Array.from(section2.childNodes).forEach((c) => {
        let nodeName = c.nodeName;
        if (nodeName == 'subsection') {
          let n = c.getAttribute('name');
          let t = c.textContent;
          abstract += `<p><b>${n}</b>&nbsp;${t}</p>`;
        } else if (nodeName == 'para') {
          let t = c.textContent;
          abstract += `<p>${t}</p>`;
        }
      });
      this.rxResearch.abstract = abstract;
    },
  }
}
</script>

<style scoped>
.mobile-container { padding: 8px; font-size:13px }
.search { display:flex; gap:6px; margin-bottom:8px }
input { flex:1; padding:6px; border:1px solid #ccc; border-radius:4px; font-size:13px }
button{ padding:6px 10px; font-size:13px }
.loader{ color:#666 }
.error{ color:#b00 }
.card{ margin-bottom:6px; padding:8px; border-radius:6px; background:#fff; box-shadow:0 0 0 1px #eee }
.sub{ color:#444; font-size:12px; margin-top:4px }
</style>